import React, {
  forwardRef,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react"
import Form from "react-bootstrap/Form"

import useKeyPress from "@atoms/UseKeyPress/UseKeyPress"
import useOutsideClick from "@atoms/UseOutsideClick/UseOutsideClick"
import { SiteContext } from "@context/Provider"

const SelectHybrid = forwardRef(
  (
    {
      json,
      numberArray,
      active,
      setActive,
      defaultValue,
      label,
      name,
      id,
      icon,
      showLabels,
      className,
      description,
      isInvalid,
    },
    ref
  ) => {
    const [open, setOpen] = useState(false)
    const [focused, setFocused] = useState(false)
    const [value, setValue] = useState(active)
    const hybridRef = useRef()

    const context = useContext(SiteContext)

    useEffect(() => {
      if (active) {
        setValue(active)
      }
    }, [active])

    useEffect(() => {
      if (value && setActive && value !== active) {
        setActive(value)
      }
    }, [active, context, setActive, value])

    // Native select

    const nativeItems = json?.map((option) => {
      return (
        <option key={option.value} value={option.value}>
          {option.text}
        </option>
      )
    })

    const handleNativeChange = (e) => setValue(e.target.value)

    // Custom select

    const customItems = json?.map((option, index) => {
      return (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div
          key={index}
          className={`hybrid-select-custom-option ${
            focused === option ? "state--active" : ""
          }`}
          option={option}
          onMouseEnter={() => setFocused(option?.value)}
          onClick={() => setSelectValue(option?.value)}
        >
          {option.text}
        </div>
      )
    })

    const setSelectValue = (value) => {
      setValue(value)
      setOpen(false)
    }

    useOutsideClick(hybridRef, () => {
      setOpen(false)
    })

    // Navigating the custom element with a keyboard

    const downPress = useKeyPress("ArrowDown")
    useEffect(() => {
      if (open && focused && downPress) {
        let index = json.indexOf(focused)
        if (index < json.length - 1) {
          setFocused(json[index + 1])
        }
      }
      // If you're wondering why the following line is there, ask Tuomo
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [downPress])

    const upPress = useKeyPress("ArrowUp")

    useEffect(() => {
      if (open && focused && upPress) {
        let index = json.indexOf(focused)
        if (index > 0) {
          setFocused(json[index - 1])
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [upPress])

    const enterPress = useKeyPress("Enter")

    useEffect(() => {
      if (open && focused && enterPress) {
        setSelectValue(focused)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enterPress])

    const spacePress = useKeyPress(" ")

    useEffect(() => {
      if (open && focused && spacePress) {
        setSelectValue(focused)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [spacePress])

    return (
      <Form.Group className={className}>
        <span
          className={`hybrid-label ${!showLabels ? "screen-reader-text" : ""}`}
          id={id}
        >
          {label}
        </span>

        <div
          className={`hybrid-select-wrapper ${isInvalid ? "is-invalid" : ""}`}
        >
          <Form.Control
            ref={ref}
            name={name}
            id={name + "-native"}
            as="select"
            className="hybrid-select-native"
            aria-labelledby={id}
            onChange={(e) => handleNativeChange(e)}
            value={value}
          >
            {!value && <option value="">{defaultValue}</option>}
            {nativeItems}
          </Form.Control>
          <div
            className={`hybrid-select-custom ${
              icon ? "hybrid-select-custom-has-icon" : ""
            }`}
            aria-hidden={!open}
            data-open={open}
            ref={hybridRef}
          >
            <div // eslint-disable-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
              className={`form-control hybrid-select-custom-trigger ${
                isInvalid ? "is-invalid" : ""
              }`}
              onClick={() => setOpen(!open)}
            >
              {icon && (
                <div
                  className={`hybrid-select-custom-icon hybrid-select-custom-icon-${icon}`}
                ></div>
              )}
              {value && json && (
                <>
                  {
                    json.filter((option) => {
                      return option?.value === value
                    })[0]?.text
                  }
                </>
              )}
              {value && !json && <>{value}</>}
              {!value && <>{defaultValue}</>}
            </div>
            <div className="hybrid-select-custom-options">{customItems}</div>
          </div>
        </div>
        {description && (
          <Form.Text
            className={`${isInvalid ? "invalid-feedback" : "text-muted"}`}
          >
            {description}
          </Form.Text>
        )}
      </Form.Group>
    )
  }
)

SelectHybrid.defaultProps = {
  showLabels: true,
}

export default SelectHybrid
